/**
 * RTH (regular trading hours) session helpers for 1-minute bars.
 * Session window: 06:30–13:00 America/Los_Angeles (390 minutes, index 0..389).
 * Extends the timezone conventions used in ./session.js.
 */

export const RTH_TZ = 'America/Los_Angeles';
export const RTH_START = { hour: 6, minute: 30 };
export const RTH_END = { hour: 13, minute: 0 };
export const RTH_SESSION_MINUTES = 390;

/** Hard-coded US equity/futures half-day sessions, 2025-2027. */
export const HALF_DAY_DATES = new Set([
  '2025-07-03', // day before Independence Day
  '2025-11-28', // day after Thanksgiving
  '2025-12-24', // Christmas Eve
  '2026-07-03', // Independence Day observed Fri Jul 3 2026 (Jul 4 is Sat)
  '2026-11-27', // day after Thanksgiving
  '2026-12-24', // Christmas Eve
  '2027-11-26', // day after Thanksgiving
  '2027-12-24', // Christmas Eve
]);

function barTimestamp(bar) {
  if (!bar) return null;
  if (Number.isFinite(bar.timestamp)) return bar.timestamp;
  if (Number.isFinite(bar.timeSec)) return bar.timeSec * 1000;
  return null;
}

export function zonedPartsAt(ts, timeZone = RTH_TZ) {
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d?.getTime?.())) return null;
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
  });
  const parts = fmt.formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    weekday: weekdayMap[get('weekday')] ?? null,
  };
}

export function dateKeyFromParts(parts) {
  if (!parts) return null;
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

export function minuteOfSessionFromParts(parts) {
  if (!parts) return null;
  const hm = parts.hour * 60 + parts.minute;
  const startHm = RTH_START.hour * 60 + RTH_START.minute;
  const endHm = RTH_END.hour * 60 + RTH_END.minute;
  if (hm < startHm || hm >= endHm) return null;
  return hm - startHm;
}

export function isWednesday(parts) {
  return parts?.weekday === 3;
}

export function isHalfDaySession(dateKey) {
  return HALF_DAY_DATES.has(dateKey);
}

/**
 * Build a per-bar session-info array: { dateKey, minuteOfSession, sessionIdx,
 * isWednesday, isHalfDay, isRth, isFirstOfSession, isLastOfSession }.
 */
export function buildSessionIndex(bars) {
  const n = bars?.length || 0;
  const out = new Array(n).fill(null);
  const order = new Map();
  let lastDateKey = null;
  for (let i = 0; i < n; i++) {
    const ts = barTimestamp(bars[i]);
    const parts = ts != null ? zonedPartsAt(ts) : null;
    const dateKey = dateKeyFromParts(parts);
    const minuteOfSession = minuteOfSessionFromParts(parts);
    const isRth = minuteOfSession != null;
    if (isRth && !order.has(dateKey)) order.set(dateKey, order.size);
    const sessionIdx = isRth ? order.get(dateKey) : null;
    out[i] = {
      dateKey,
      minuteOfSession,
      sessionIdx,
      isWednesday: isRth ? isWednesday(parts) : false,
      isHalfDay: isRth ? isHalfDaySession(dateKey) : false,
      isRth,
      isFirstOfSession: isRth && dateKey !== lastDateKey,
    };
    if (isRth) lastDateKey = dateKey;
  }
  // mark last bar of each session
  for (let i = 0; i < n; i++) {
    if (!out[i]?.isRth) continue;
    const next = out[i + 1];
    out[i].isLastOfSession = !next || !next.isRth || next.dateKey !== out[i].dateKey;
  }
  return out;
}

/**
 * Per-bar isThinSession flag, decided once cumulative volume at `checkMinute`
 * is known, held constant for the rest of that session. Compares the
 * session's volume at `checkMinute` against the median of the trailing
 * `lookbackSessions` sessions' volume at the same minute.
 */
export function thinSessionFlags(bars, sessionInfo, { checkMinute = 60, thinVolPct = 0.5, lookbackSessions = 10 } = {}) {
  const n = bars?.length || 0;
  const out = new Array(n).fill(false);
  const cumBySession = new Map(); // sessionIdx -> cumulative volume so far
  const checkpointBySession = new Map(); // sessionIdx -> volume at checkMinute
  const sessionOrder = [];
  const thinBySession = new Map();

  for (let i = 0; i < n; i++) {
    const info = sessionInfo[i];
    if (!info?.isRth) continue;
    const { sessionIdx, minuteOfSession } = info;
    if (!cumBySession.has(sessionIdx)) {
      cumBySession.set(sessionIdx, 0);
      sessionOrder.push(sessionIdx);
    }
    const vol = Number(bars[i]?.volume) || 0;
    const cum = cumBySession.get(sessionIdx) + vol;
    cumBySession.set(sessionIdx, cum);

    if (minuteOfSession === checkMinute && !checkpointBySession.has(sessionIdx)) {
      checkpointBySession.set(sessionIdx, cum);
      const priorIdxs = sessionOrder.filter((s) => s < sessionIdx).slice(-lookbackSessions);
      const priorVols = priorIdxs.map((s) => checkpointBySession.get(s)).filter(Number.isFinite);
      if (priorVols.length >= 3) {
        const sorted = [...priorVols].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
        thinBySession.set(sessionIdx, median > 0 && cum < thinVolPct * median);
      } else {
        thinBySession.set(sessionIdx, false);
      }
    }
    out[i] = Boolean(thinBySession.get(sessionIdx));
  }
  return out;
}
